import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sparkles, Loader2, Copy, BookmarkPlus, Check, FileText, Crosshair } from "lucide-react";
import { copyText } from "@/lib/clipboard";

// AI Scenario Builder: "what if sales grow 20%" -> parameter values (+optional save).
export function AiScenarioDialog({ open, onOpenChange, defs = [], scenarios = [], onApply, onSaveScenario }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);   // {name, values, rationale}
  useEffect(() => { if (open) { setResult(null); setPrompt(""); } }, [open]);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the scenario first"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/scenario-build", {
        prompt: prompt.trim(), defs, existing_names: scenarios.map((s) => s.name),
      }, { timeout: 60000 });
      setResult(data);
      onApply(data.values);
      toast.success("Scenario values applied — sliders updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI scenario generation failed");
    } finally { setLoading(false); }
  };
  const save = () => {
    if (!result) return;
    onSaveScenario({ name: result.name, values: result.values });
    onOpenChange(false);
  };
  const label = (n) => defs.find((d) => d.name === n)?.label || n;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="w-5 h-5 text-[color:var(--brand-mint)]" />AI scenario builder</DialogTitle>
          <DialogDescription>Describe a what-if in plain language — AI maps it onto this dashboard's parameters ({defs.map((d) => d.label || d.name).join(", ")}).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <Textarea data-testid="ai-scenario-prompt" rows={3} className="bg-[color:var(--surface)] text-sm" autoFocus
            placeholder='e.g. "what if sales grow 20% next quarter"' value={prompt} onChange={(e) => setPrompt(e.target.value)} />
          {result && (
            <div data-testid="ai-scenario-result" className="rounded-xl border border-[color:var(--glass-border)] bg-[color:rgba(0,194,168,0.06)] p-3">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-[color:var(--ink)]"><Check className="w-4 h-4 text-[color:var(--success)]" />{result.name}</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {Object.entries(result.values || {}).map(([n, v]) => (
                  <span key={n} className="text-[11px] px-2 py-0.5 rounded-full bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[color:var(--ink-2)]">
                    {label(n)}: <b className="tabular-nums">{Number(v).toLocaleString(undefined, { maximumFractionDigits: 3 })}</b>
                  </span>
                ))}
              </div>
              {result.rationale && <div className="mt-2 text-[11px] text-[color:var(--ink-3)] leading-relaxed">{result.rationale}</div>}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {result && onSaveScenario && (
            <Button data-testid="ai-scenario-save" variant="outline" onClick={save} className="bg-[color:var(--surface)]">
              <BookmarkPlus className="w-4 h-4 mr-1" />Save as scenario
            </Button>
          )}
          <Button data-testid="ai-scenario-generate" onClick={generate} disabled={loading} className="bg-[color:var(--brand-mint)] text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}{result ? "Regenerate" : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// AI scenario narrative: board-deck paragraph explaining baseline vs scenario.
export function ScenarioNarrativeDialog({ open, onOpenChange, dashboardId, scenarioName, params, baselineParams }) {
  const [loading, setLoading] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [comparisons, setComparisons] = useState([]);

  useEffect(() => {
    if (!open || !dashboardId) return;
    let active = true;
    setLoading(true); setNarrative(""); setComparisons([]);
    api.post("/ai/scenario-narrative", {
      dashboard_id: dashboardId, scenario_name: scenarioName || "Scenario",
      params: params || {}, baseline_params: baselineParams || {},
    }, { timeout: 90000 })
      .then(({ data }) => { if (active) { setNarrative(data.narrative || ""); setComparisons(data.comparisons || []); } })
      .catch((e) => { if (active) setNarrative(e?.response?.data?.detail || "Narrative generation failed. Check your AI settings."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // refetch whenever the dialog opens for the current params
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copy = async () => {
    const ok = await copyText(narrative);
    if (ok) toast.success("Narrative copied");
    else toast.error("Could not copy automatically — select the text and copy manually");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileText className="w-5 h-5 text-[color:var(--brand-ocean)]" />{scenarioName || "Scenario"} — narrative</DialogTitle>
          <DialogDescription>AI-written summary of what changes vs the baseline — ready to paste into a board deck.</DialogDescription>
        </DialogHeader>
        <div data-testid="scenario-narrative-text" className="py-1 text-sm leading-relaxed text-[color:var(--ink-2)] min-h-[90px]">
          {loading ? (
            <div className="flex items-center gap-2 text-[color:var(--ink-3)]"><Loader2 className="w-4 h-4 animate-spin" />Comparing baseline vs scenario…</div>
          ) : narrative}
        </div>
        {!loading && comparisons.length > 0 && (
          <div className="max-h-40 overflow-auto rounded-lg border border-[color:var(--glass-border)]">
            <table className="w-full text-[11px]">
              <thead><tr className="text-[color:var(--ink-3)]">
                <th className="text-left px-2 py-1 font-medium">Measure</th>
                <th className="text-right px-2 py-1 font-medium">Baseline</th>
                <th className="text-right px-2 py-1 font-medium">Scenario</th>
                <th className="text-right px-2 py-1 font-medium">Δ%</th>
              </tr></thead>
              <tbody>
                {comparisons.map((c, i) => (
                  <tr key={i} className="border-t border-[color:var(--glass-border)]">
                    <td className="px-2 py-1 text-[color:var(--ink-2)]">{c.widget} · {c.measure}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{Number(c.baseline).toLocaleString()}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-semibold">{Number(c.scenario).toLocaleString()}</td>
                    <td className={"px-2 py-1 text-right tabular-nums font-semibold " + ((c.delta_pct ?? 0) >= 0 ? "text-[color:var(--success)]" : "text-[color:var(--danger)]")}>
                      {c.delta_pct != null ? `${c.delta_pct >= 0 ? "+" : ""}${c.delta_pct}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button data-testid="scenario-narrative-copy" variant="outline" onClick={copy} disabled={loading || !narrative} className="bg-[color:var(--surface)]">
            <Copy className="w-4 h-4 mr-1" />Copy narrative
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Goal Seek (Item 95): solve which value of one what-if parameter hits a target measure value.
export function GoalSeekDialog({ open, onOpenChange, defs = [], values = {}, onApply, ctx }) {
  const widgets = (ctx?.widgets || []).filter((w) => w.dataset_id && (w.config?.measures || []).length);
  const measureOpts = [];
  widgets.forEach((w) => (w.config.measures || []).forEach((m) => {
    const key = m.key || m.alias || `${m.agg || "sum"}_${m.field}`;
    measureOpts.push({ id: `${w.id}::${key}`, w, key, label: `${w.title || w.type} · ${m.label || key}` });
  }));
  const [sel, setSel] = useState("");
  const [target, setTarget] = useState("");
  const [param, setParam] = useState(defs[0]?.name || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setResult(null); setSel(measureOpts[0]?.id || ""); setParam(defs[0]?.name || ""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const solve = async () => {
    const opt = measureOpts.find((o) => o.id === sel);
    const def = defs.find((d) => d.name === param);
    if (!opt || !def) { toast.error("Pick a target measure and a parameter"); return; }
    if (target === "" || !Number.isFinite(Number(target))) { toast.error("Enter a numeric target value"); return; }
    setLoading(true); setResult(null);
    try {
      const sq = ctx.getQuery(opt.w);
      const fixed = {};
      defs.forEach((d) => { if (d.name !== param) fixed[d.name] = values[d.name] !== undefined ? values[d.name] : (d.default ?? 0); });
      const { data } = await api.post(`/datasets/${opt.w.dataset_id}/model/goal-seek`, {
        measure: opt.key, target: Number(target), param,
        param_min: def.min ?? 0, param_max: def.max ?? 1,
        fixed_params: fixed, semantic_query: sq,
      }, { timeout: 120000 });
      setResult({ ...data, def, measureLabel: opt.label });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Goal seek failed");
    } finally { setLoading(false); }
  };
  const apply = () => {
    if (!result) return;
    const step = Number(result.def?.step) || 0;
    let v = Number(result.value);
    if (step > 0) v = Math.round(v / step) * step;
    v = Math.min(Number(result.def?.max ?? v), Math.max(Number(result.def?.min ?? v), v));
    onApply({ ...values, [result.param]: Number(v.toFixed(6)) });
    toast.success(`${result.def?.label || result.param} set to ${v.toLocaleString(undefined, { maximumFractionDigits: 4 })}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Crosshair className="w-5 h-5 text-[color:var(--brand-ocean)]" />Goal seek</DialogTitle>
          <DialogDescription>Pick a target (e.g. profit = 1M) — the app solves which parameter value reaches it, holding other parameters at their current values.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <label className="text-[10px] font-medium text-[color:var(--ink-3)]">Target measure (from a widget)</label>
            <select data-testid="goalseek-measure-select" className="mt-1 w-full h-9 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2 text-sm"
              value={sel} onChange={(e) => setSel(e.target.value)}>
              {measureOpts.length === 0 && <option value="">No widgets with measures</option>}
              {measureOpts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-medium text-[color:var(--ink-3)]">Target value</label>
              <input data-testid="goalseek-target-input" type="number" className="mt-1 w-full h-9 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2 text-sm tabular-nums"
                value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 1000000" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-[color:var(--ink-3)]">Adjust parameter</label>
              <select data-testid="goalseek-param-select" className="mt-1 w-full h-9 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2 text-sm"
                value={param} onChange={(e) => setParam(e.target.value)}>
                {defs.map((d) => <option key={d.name} value={d.name}>{d.label || d.name}</option>)}
              </select>
            </div>
          </div>
          {result && (
            <div data-testid="goalseek-result" className={"rounded-xl border p-3 " + (result.achievable ? "border-[color:var(--glass-border)] bg-[color:rgba(43,78,255,0.05)]" : "border-amber-200 bg-amber-50/60")}>
              <div className="text-sm text-[color:var(--ink-2)]">
                Set <b>{result.def?.label || result.param}</b> to{" "}
                <b className="tabular-nums text-[color:var(--brand-ocean)]" data-testid="goalseek-solution">{Number(result.value).toLocaleString(undefined, { maximumFractionDigits: 4 })}</b>
              </div>
              <div className="mt-1 text-[11px] text-[color:var(--ink-3)]">
                Achieves <b className="tabular-nums">{Number(result.achieved).toLocaleString(undefined, { maximumFractionDigits: 2 })}</b> vs target{" "}
                <b className="tabular-nums">{Number(result.target).toLocaleString(undefined, { maximumFractionDigits: 2 })}</b> · {result.iterations} evaluations
              </div>
              {result.message && <div className="mt-1 text-[11px] text-amber-700">{result.message}</div>}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {result && <Button data-testid="goalseek-apply" variant="outline" onClick={apply} className="bg-[color:var(--surface)]"><Check className="w-4 h-4 mr-1" />Apply to sliders</Button>}
          <Button data-testid="goalseek-solve" onClick={solve} disabled={loading || !measureOpts.length} className="bg-[color:var(--brand-ocean)] text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Crosshair className="w-4 h-4 mr-1" />}{result ? "Re-solve" : "Solve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
